/**
 * HTTP session API for remote v1 — session lifecycle + bootstrap only (create, GET, PATCH state/end).
 * Deck commands and emotes use MQTT from the browser, not these endpoints.
 * `createDevMockRemoteSessionApi` is for same-origin local demos only.
 */

import type { LecturaConfig } from '../../../core/types';
import { warnLecturaNonProduction } from '../../../lib/lectura-non-production-log';
import { parseRemoteV1StatePayload, type RemoteV1StatePayload } from './protocol';
import { appendRemotePreviewQueryParams } from './remote-preview-url';

/** localStorage key for `createDevMockRemoteSessionApi` session map (E2E may seed this). */
export const REMOTE_V1_DEV_SESSIONS_STORAGE_KEY = 'lectura-next:remote-v1:dev-sessions';

const DEV_STORAGE_KEY = REMOTE_V1_DEV_SESSIONS_STORAGE_KEY;

export interface RemoteV1MqttAuth {
  mode: string;
  username: string;
  password: string;
}

export interface RemoteV1Bootstrap {
  session: {
    id: string;
    status: string;
    presentation_id: string;
    created_at?: string;
    ended_at?: string | null;
  };
  presentation: {
    id: string;
    iframe_url: string;
    title?: string;
  };
  mqtt: {
    url: string;
    client_id_prefix?: string;
    auth: RemoteV1MqttAuth | null;
  };
  role: 'presenter' | 'viewer' | 'display';
  state: RemoteV1StatePayload;
  /** Server-minted id for this tab/device for the session (never generate in UI). */
  participant_id: string;
  /** When backend ties the participant to Firebase Auth (audit / ACL). */
  firebase_uid?: string;
  /** Required for presenter to validate inbound commands when using dev broker. */
  command_token?: string;
}

export interface CreatePresenterSessionResultV1 {
  session: RemoteV1Bootstrap['session'];
  remote_path: string;
  /** Presenter uses these for MQTT without a second GET. */
  command_token: string;
  iframe_url: string;
  mqtt_url: string;
  presentation_id: string;
  /** Issued by POST handler (same trust model as session id). */
  participant_id: string;
  firebase_uid?: string;
}

function trimBase(url: string): string {
  return String(url || '').trim().replace(/\/+$/, '');
}

/** Thrown when POST /sessions or other session HTTP calls return a non-2xx status. */
export class RemoteSessionHttpError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'RemoteSessionHttpError';
    this.status = status;
    this.code = code;
  }
}

function messageFromSessionErrorJson(body: unknown, status: number, fallback: string): string {
  if (body && typeof body === 'object' && 'message' in body) {
    const m = (body as { message?: unknown }).message;
    if (typeof m === 'string' && m.trim()) return m.trim();
  }
  return fallback || `Request failed (${status})`;
}

function errorCodeFromSessionErrorJson(body: unknown): string | undefined {
  if (body && typeof body === 'object' && 'error' in body) {
    const e = (body as { error?: unknown }).error;
    if (typeof e === 'string' && e.trim()) return e.trim();
  }
  return undefined;
}

/**
 * Primary deployment for session API.
 * Use the direct Cloud Run URL for reliability across domains where Hosting rewrites
 * are unavailable/misconfigured.
 */
export const LECTURA_REMOTE_SESSION_API_PRODUCTION_ORIGIN =
  'https://lecturaremoteapi-rj2iai7w2a-uc.a.run.app';

/** Same as {@link LECTURA_REMOTE_SESSION_API_PRODUCTION_ORIGIN} — kept for older imports/docs. */
export const LECTURA_DEFAULT_REMOTE_SESSION_API_ORIGIN = LECTURA_REMOTE_SESSION_API_PRODUCTION_ORIGIN;

/**
 * Hosts that should NOT call same-origin `/api/**` and must use the shared
 * remote-session API origin instead.
 */
const STAGING_HOSTS_USE_PRODUCTION_SESSION_API = new Set([
  'lectura-beta.web.app',
  'lectura.be',
  'www.lectura.be'
]);

/**
 * `lectura.be` is often deployed as static/CDN without Firebase Hosting rewrites for `/api/**`.
 * Pages may still set `remote.v1.apiBaseUrl` to same-origin — that would 404. Treat as unset
 * so we fall back to the Firebase project that hosts `lecturaRemoteApi`.
 */
function hasKnownBrokenSameOriginSessionApi(configuredBase: string): boolean {
  const raw = String(configuredBase ?? '').trim();
  if (!raw) return false;
  try {
    const url = raw.includes('://') ? new URL(raw) : new URL(`https://${raw}`);
    const h = url.hostname.toLowerCase();
    return (
      h === 'lectura.be' ||
      h === 'www.lectura.be' ||
      h === 'founderstable-firebase.web.app'
    );
  } catch {
    return false;
  }
}

/**
 * Effective session API origin when `remote.v1.apiBaseUrl` is omitted.
 *
 * - Explicit `remote.v1.apiBaseUrl` wins.
 * - Then `window.__LECTURA_REMOTE_SESSION_API_ORIGIN__` (any host; use for forks / staging).
 * - Known staging hostnames (e.g. beta) → production API so `/api` is not required on that project.
 * - Otherwise non-localhost → same origin (Hosting rewrites `/api/**` to Functions).
 * - Localhost → {@link LECTURA_DEFAULT_REMOTE_SESSION_API_ORIGIN} unless `__LECTURA_REMOTE_SESSION_API_ORIGIN__` is set.
 */
export function resolveDefaultRemoteSessionApiBaseUrl(configuredBase: string | undefined): string {
  let explicit = String(configuredBase ?? '').trim();
  if (explicit && hasKnownBrokenSameOriginSessionApi(explicit)) {
    explicit = '';
  }
  if (explicit) return explicit;
  if (typeof window === 'undefined') return '';
  const win = window as Window & { __LECTURA_REMOTE_SESSION_API_ORIGIN__?: string };
  let globalOverride = trimBase(String(win.__LECTURA_REMOTE_SESSION_API_ORIGIN__ || ''));
  if (globalOverride && hasKnownBrokenSameOriginSessionApi(globalOverride)) {
    globalOverride = '';
  }
  if (globalOverride) return globalOverride;

  const { hostname, origin } = window.location;
  if (STAGING_HOSTS_USE_PRODUCTION_SESSION_API.has(hostname)) {
    return LECTURA_REMOTE_SESSION_API_PRODUCTION_ORIGIN;
  }
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') return origin;
  return LECTURA_DEFAULT_REMOTE_SESSION_API_ORIGIN;
}

function randomBase64Url(bytes: number): string {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  let binary = '';
  for (let i = 0; i < arr.length; i++) binary += String.fromCharCode(arr[i] as number);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

/** Matches `functions/remote-sessions-api.js` (short ids for URLs / QR). */
const DEV_SESSION_ID_LEN = 4;
const DEV_SESSION_ID_ALPHABET = '23456789abcdefghjkmnpqrstuvwxyz';

function randomShortSessionId(): string {
  const arr = new Uint8Array(DEV_SESSION_ID_LEN);
  crypto.getRandomValues(arr);
  let out = '';
  for (let i = 0; i < DEV_SESSION_ID_LEN; i++) {
    out += DEV_SESSION_ID_ALPHABET[arr[i] % DEV_SESSION_ID_ALPHABET.length];
  }
  return out;
}

function initialState(): RemoteV1StatePayload {
  const now = new Date().toISOString();
  return {
    slide: { h: 0, v: 0, f: 0 },
    mode: 'live',
    meta: { title: '', notes_plain: '', slides_version: '', total_slides: 0 },
    version: 0,
    updated_at: now
  };
}

interface StoredDevSession {
  presentationId: string;
  iframeUrl: string;
  mqttUrl: string;
  commandToken: string;
  /** Simulates server-minted presenter participant for this session. */
  presenterParticipantId: string;
  status: 'active' | 'ended';
  lastState: RemoteV1StatePayload;
}

function loadDevMap(): Record<string, StoredDevSession> {
  try {
    const raw = localStorage.getItem(DEV_STORAGE_KEY);
    if (!raw) return {};
    const p = JSON.parse(raw) as unknown;
    return p && typeof p === 'object' && !Array.isArray(p) ? (p as Record<string, StoredDevSession>) : {};
  } catch {
    return {};
  }
}

function saveDevMap(m: Record<string, StoredDevSession>): void {
  try {
    localStorage.setItem(DEV_STORAGE_KEY, JSON.stringify(m));
  } catch {
    // ignore
  }
}

function strField(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

/**
 * Normalizes POST /api/presentations/:id/sessions JSON (camelCase or snake_case).
 * Server must return participant_id; we do not mint it in the browser.
 */
export function normalizeCreatePresenterSessionResponse(
  raw: unknown,
  fallbackPresentationId: string
): CreatePresenterSessionResultV1 {
  if (!raw || typeof raw !== 'object') throw new Error('createSession: invalid JSON body');
  const o = raw as Record<string, unknown>;
  const session = o.session;
  if (!session || typeof session !== 'object') throw new Error('createSession: missing session object');
  const s = session as Record<string, unknown>;
  const sessionId = strField(s, 'id');
  if (!sessionId) throw new Error('createSession: missing session.id');

  const participantId = strField(o, 'participant_id', 'participantId');
  if (!participantId) {
    throw new Error(
      'createSession: missing participant_id — backend must mint one per presenter device/session'
    );
  }

  const presentationId =
    strField(s, 'presentation_id', 'presentationId') ||
    strField(o, 'presentation_id', 'presentationId') ||
    fallbackPresentationId;

  const remotePath = strField(o, 'remote_path', 'remotePath') || `/remote/${sessionId}`;
  const commandToken = strField(o, 'command_token', 'commandToken');
  if (!commandToken) throw new Error('createSession: missing command_token');

  const iframeUrl = strField(o, 'iframe_url', 'iframeUrl');
  if (!iframeUrl) throw new Error('createSession: missing iframe_url');

  const mqttUrl =
    strField(o, 'mqtt_url', 'mqttUrl') ||
    (typeof o.mqtt === 'object' && o.mqtt !== null
      ? strField(o.mqtt as Record<string, unknown>, 'url')
      : '');
  if (!mqttUrl) throw new Error('createSession: missing mqtt_url (or mqtt.url)');

  const firebaseUid = strField(o, 'firebase_uid', 'firebaseUid');
  const out: CreatePresenterSessionResultV1 = {
    session: {
      id: sessionId,
      status: strField(s, 'status') || 'active',
      presentation_id: presentationId
    },
    remote_path: remotePath,
    command_token: commandToken,
    iframe_url: iframeUrl,
    mqtt_url: mqttUrl,
    presentation_id: presentationId,
    participant_id: participantId
  };
  if (firebaseUid) out.firebase_uid = firebaseUid;
  return out;
}

export interface CreatePresenterSessionOptionsV1 {
  /** Deck path for iframe, e.g. `/workshop` (passed to POST as `embedBasePath`). */
  embedBasePath?: string;
  /** For console logs only (not sent to the server). Set `window.__LECTURA_DEBUG_REMOTE_SESSION__ = true` for stack traces. */
  debugCaller?: string;
}

export interface RemoteSessionApiV1 {
  createPresenterSession(
    presentationId: string,
    options?: CreatePresenterSessionOptionsV1
  ): Promise<CreatePresenterSessionResultV1>;
  getBootstrap(sessionId: string): Promise<RemoteV1Bootstrap>;
  /**
   * Presenter: persist slide state to the server (Firestore) so new viewers get a fresh bootstrap
   * even if MQTT retained state is missing or slow.
   */
  patchSessionState(
    sessionId: string,
    state: RemoteV1StatePayload,
    init?: { signal?: AbortSignal }
  ): Promise<void>;
  /** Presenter: end session (viewers get 410 on next bootstrap). */
  endPresenterSession(sessionId: string, init?: { signal?: AbortSignal; keepalive?: boolean }): Promise<void>;
}

async function resolveAuthHeaders(
  getAuthHeaders?: () => Record<string, string> | Promise<Record<string, string>>
): Promise<Record<string, string>> {
  if (!getAuthHeaders) return {};
  return Promise.resolve(getAuthHeaders()).then((h) => (h && typeof h === 'object' ? h : {}));
}

function remoteSessionDebugVerbose(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window as unknown as { __LECTURA_DEBUG_REMOTE_SESSION__?: boolean }).__LECTURA_DEBUG_REMOTE_SESSION__ === true
  );
}

function logCreatePresenterSessionAttempt(meta: {
  presentationId: string;
  caller: string;
  hasAuthorizationHeader: boolean;
  apiBase: string;
}): void {
  if (typeof console === 'undefined' || typeof console.debug !== 'function') return;
  console.debug('[lectura-next][remote-session] POST /sessions (attempt)', meta);
  if (remoteSessionDebugVerbose()) {
    console.debug('[lectura-next][remote-session] stack', new Error('createPresenterSession').stack);
  }
}

function logCreatePresenterSessionFailure(meta: {
  presentationId: string;
  caller: string;
  status: number;
  hasAuthorizationHeader: boolean;
}): void {
  if (typeof console === 'undefined' || typeof console.warn !== 'function') return;
  console.warn('[lectura-next][remote-session] POST /sessions failed', meta);
}

export function createHttpRemoteSessionApi(
  apiBaseUrl: string,
  getAuthHeaders?: () => Record<string, string> | Promise<Record<string, string>>
): RemoteSessionApiV1 {
  const base = trimBase(apiBaseUrl);
  if (typeof window !== 'undefined' && window.location?.origin && base !== window.location.origin) {
    warnLecturaNonProduction('Session HTTP API base URL differs from this page origin.', {
      apiBaseUrl: base,
      pageOrigin: window.location.origin
    });
  }

  async function createPresenterSession(
    presentationId: string,
    options?: CreatePresenterSessionOptionsV1
  ): Promise<CreatePresenterSessionResultV1> {
    const auth = await resolveAuthHeaders(getAuthHeaders);
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...auth
    };
    const caller = String(options?.debugCaller || '').trim() || 'unknown';
    const hasAuthorizationHeader = typeof auth.Authorization === 'string' && auth.Authorization.length > 0;
    logCreatePresenterSessionAttempt({
      presentationId,
      caller,
      hasAuthorizationHeader,
      apiBase: base
    });
    const body: Record<string, string> = {};
    const ebp = String(options?.embedBasePath || '').trim();
    if (ebp) body.embedBasePath = ebp.startsWith('/') ? ebp : `/${ebp}`;
    const res = await fetch(`${base}/api/presentations/${encodeURIComponent(presentationId)}/sessions`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      logCreatePresenterSessionFailure({
        presentationId,
        caller,
        status: res.status,
        hasAuthorizationHeader
      });
      let errBody: unknown = null;
      try {
        errBody = await res.json();
      } catch {
        /* non-JSON body */
      }
      const msg = messageFromSessionErrorJson(errBody, res.status, `createSession failed: ${res.status}`);
      const code = errorCodeFromSessionErrorJson(errBody);
      throw new RemoteSessionHttpError(msg, res.status, code);
    }
    const json: unknown = await res.json();
    return normalizeCreatePresenterSessionResponse(json, presentationId);
  }

  async function getBootstrap(sessionId: string): Promise<RemoteV1Bootstrap> {
    const auth = await resolveAuthHeaders(getAuthHeaders);
    const res = await fetch(`${base}/api/sessions/${encodeURIComponent(sessionId)}`, { headers: { ...auth } });
    if (res.status === 404) throw new Error('session not found');
    if (res.status === 410) throw new Error('session ended');
    if (!res.ok) throw new Error(`getBootstrap failed: ${res.status}`);
    return normalizeBootstrap(await res.json());
  }

  async function patchSessionState(
    sessionId: string,
    state: RemoteV1StatePayload,
    init?: { signal?: AbortSignal }
  ): Promise<void> {
    const auth = await resolveAuthHeaders(getAuthHeaders);
    const res = await fetch(`${base}/api/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...auth
      },
      body: JSON.stringify({ state }),
      signal: init?.signal
    });
    if (res.status === 410) throw new Error('session ended');
    if (!res.ok) throw new Error(`patchSessionState failed: ${res.status}`);
  }

  async function endPresenterSession(
    sessionId: string,
    init?: { signal?: AbortSignal; keepalive?: boolean }
  ): Promise<void> {
    const auth = await resolveAuthHeaders(getAuthHeaders);
    const res = await fetch(`${base}/api/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...auth
      },
      body: JSON.stringify({ action: 'end' }),
      signal: init?.signal,
      keepalive: init?.keepalive === true
    });
    if (res.status === 404) throw new Error('session not found');
    if (!res.ok) throw new Error(`endPresenterSession failed: ${res.status}`);
  }

  return {
    createPresenterSession,
    getBootstrap,
    patchSessionState,
    endPresenterSession
  };
}

function normalizeBootstrap(raw: unknown): RemoteV1Bootstrap {
  if (!raw || typeof raw !== 'object') throw new Error('invalid bootstrap');
  const o = raw as Record<string, unknown>;
  const session = o.session as Record<string, unknown> | undefined;
  const presentation = o.presentation as Record<string, unknown> | undefined;
  const mqtt = o.mqtt as Record<string, unknown> | undefined;
  if (!session?.id || !presentation?.iframe_url || !mqtt?.url) throw new Error('invalid bootstrap shape');
  const stateRaw = o.state;
  const sid = String(session.id);
  const presId = String(session.presentation_id || presentation.id || '');
  const parsedState =
    stateRaw && typeof stateRaw === 'object'
      ? parseRemoteV1StatePayload(stateRaw) || initialState()
      : initialState();
  return {
    session: {
      id: sid,
      status: String(session.status || 'active'),
      presentation_id: presId,
      created_at: session.created_at != null ? String(session.created_at) : undefined,
      ended_at: session.ended_at == null ? null : String(session.ended_at)
    },
    presentation: {
      id: String(presentation.id || presId),
      iframe_url: String(presentation.iframe_url),
      title: presentation.title != null ? String(presentation.title) : undefined
    },
    mqtt: {
      url: String(mqtt.url),
      client_id_prefix: mqtt.client_id_prefix != null ? String(mqtt.client_id_prefix) : undefined,
      auth:
        mqtt.auth && typeof mqtt.auth === 'object'
          ? (mqtt.auth as RemoteV1MqttAuth)
          : null
    },
    role: (o.role === 'presenter' || o.role === 'display' ? o.role : 'viewer') as RemoteV1Bootstrap['role'],
    state: parsedState,
    participant_id: (() => {
      const pid = strField(o, 'participant_id', 'participantId');
      if (!pid) throw new Error('getBootstrap: missing participant_id from server');
      return pid;
    })(),
    firebase_uid: strField(o, 'firebase_uid', 'firebaseUid') || undefined,
    command_token: o.command_token != null ? String(o.command_token) : undefined
  };
}

/**
 * Same-origin demo: sessions stored in localStorage; second tab can open `/remote/:id`.
 */
export function createDevMockRemoteSessionApi(mqttUrl: string, getDeckIframeUrl: () => string): RemoteSessionApiV1 {
  const mqtt = String(mqttUrl || '').trim() || 'wss://broker.hivemq.com:8884/mqtt';

  return {
    async createPresenterSession(
      presentationId: string,
      _options?: CreatePresenterSessionOptionsV1
    ): Promise<CreatePresenterSessionResultV1> {
      const map = loadDevMap();
      let id = '';
      for (let attempt = 0; attempt < 32; attempt++) {
        const candidate = randomShortSessionId();
        if (!map[candidate]) {
          id = candidate;
          break;
        }
      }
      if (!id) throw new Error('Could not allocate dev session id');
      const presenterParticipantId = crypto.randomUUID();
      const commandToken = randomBase64Url(24);
      const iframeUrl = appendRemotePreviewQueryParams(
        getDeckIframeUrl(),
        typeof window !== 'undefined' ? window.location.origin : undefined
      );
      const lastState = initialState();
      map[id] = {
        presentationId,
        iframeUrl,
        mqttUrl: mqtt,
        commandToken,
        presenterParticipantId,
        status: 'active',
        lastState
      };
      saveDevMap(map);
      return {
        session: {
          id,
          status: 'active',
          presentation_id: presentationId
        },
        remote_path: `/remote/${id}`,
        command_token: commandToken,
        iframe_url: iframeUrl,
        mqtt_url: mqtt,
        presentation_id: presentationId,
        participant_id: presenterParticipantId
      };
    },

    async getBootstrap(sessionId: string): Promise<RemoteV1Bootstrap> {
      const map = loadDevMap();
      const s = map[sessionId];
      if (!s || s.status !== 'active') throw new Error('session not found');
      if (!s.presenterParticipantId) {
        s.presenterParticipantId = crypto.randomUUID();
        map[sessionId] = s;
        saveDevMap(map);
      }
      const viewerParticipantId = crypto.randomUUID();
      return {
        session: {
          id: sessionId,
          status: 'active',
          presentation_id: s.presentationId
        },
        presentation: {
          id: s.presentationId,
          iframe_url: s.iframeUrl
        },
        mqtt: {
          url: s.mqttUrl,
          auth: null
        },
        role: 'viewer',
        state: s.lastState,
        participant_id: viewerParticipantId
      };
    },

    async patchSessionState(sessionId: string, state: RemoteV1StatePayload): Promise<void> {
      const map = loadDevMap();
      const s = map[sessionId];
      if (!s || s.status !== 'active') return;
      const prevV = typeof s.lastState?.version === 'number' ? s.lastState.version : 0;
      if (state.version < prevV) return;
      s.lastState = state;
      saveDevMap(map);
    },

    async endPresenterSession(sessionId: string, _init?: { signal?: AbortSignal; keepalive?: boolean }): Promise<void> {
      const map = loadDevMap();
      const s = map[sessionId];
      if (!s) throw new Error('session not found');
      s.status = 'ended';
      saveDevMap(map);
    }
  };
}

/** Deck or `/remote/` page: dev mock vs HTTP API from merged `LecturaConfig.remote.v1`. */
export function resolveRemoteSessionApi(
  config: LecturaConfig,
  mqttUrl: string,
  devMockIframeUrl: () => string
): RemoteSessionApiV1 {
  const v1 = config.remote?.v1;
  if (!v1 || v1.enabled === false) {
    throw new Error('Lectura: remote.v1 is required (defaults are applied by getConfig when remote is used)');
  }
  const mqtt = String(mqttUrl || '').trim() || 'wss://broker.hivemq.com:8884/mqtt';
  if (/hivemq\.com/i.test(mqtt)) {
    warnLecturaNonProduction(
      'MQTT URL points at public HiveMQ — fine for demos; use a dedicated broker for production ACLs.',
      { mqttUrl: mqtt }
    );
  }
  if (v1.devMock) {
    warnLecturaNonProduction('Remote session API uses devMock (localStorage, not Cloud Functions).', {
      storageKey: REMOTE_V1_DEV_SESSIONS_STORAGE_KEY
    });
    return createDevMockRemoteSessionApi(mqtt, devMockIframeUrl);
  }
  const base = resolveDefaultRemoteSessionApiBaseUrl(v1.apiBaseUrl);
  return createHttpRemoteSessionApi(base, v1.getAuthHeaders);
}
